import { z } from 'zod';
import { IdSchema, E164Schema, TimestampSchema, MoneySchema } from './common.js';

/**
 * The Node API contract (doc 05 section 10). Consumed only by arc-agent.
 *
 * CDRs arrive NORMALISED. FreeSWITCH's json_cdr is large, deeply nested and
 * version-specific -- parsing it belongs on the node, next to the switch that
 * produced it, not in the control plane. That keeps a FreeSWITCH upgrade from
 * becoming an arc-api change.
 */

export const NodeCdrSchema = z.object({
  /** FreeSWITCH channel UUID. The idempotency key for the whole pipeline. */
  id: IdSchema,
  call_uuid: IdSchema,
  tenant_id: IdSchema,
  direction: z.enum(['inbound', 'outbound', 'internal']),
  leg: z.enum(['a', 'b']).default('a'),

  from_number: z.string().max(64).nullable().optional(),
  from_name: z.string().max(128).nullable().optional(),
  to_number: z.string().max(64).nullable().optional(),
  destination_e164: E164Schema.nullable().optional(),
  extension_id: IdSchema.nullable().optional(),
  /** The trunk an outbound call left on; its carrier's cost deck prices the call. */
  trunk_id: IdSchema.nullable().optional(),

  started_at: TimestampSchema,
  answered_at: TimestampSchema.nullable().optional(),
  ended_at: TimestampSchema,
  duration_sec: z.number().int().min(0),
  billable_sec: z.number().int().min(0).default(0),

  hangup_cause: z.string().max(64).nullable().optional(),
  sip_code: z.number().int().nullable().optional(),

  mos: z.string().nullable().optional(),
  jitter_ms: z.string().nullable().optional(),
  packet_loss_pct: z.string().nullable().optional(),
  codec: z.string().max(32).nullable().optional(),

  /** What the NODE computed, so the control plane can verify it (ADR-015). */
  rated_on_node: MoneySchema.nullable().optional(),
  rate_deck_version_id: IdSchema.nullable().optional(),

  recording_path: z.string().max(512).nullable().optional(),

  /**
   * Set by the dialplan's emergency rule (arc_emergency), never inferred from
   * the digits. An emergency call is not rated or charged, and is written to
   * its own log (doc 22).
   */
  is_emergency: z.boolean().optional(),
});
export type NodeCdr = z.infer<typeof NodeCdrSchema>;

export const NodeCdrBatchSchema = z.object({
  cdrs: z.array(NodeCdrSchema).min(1).max(500),
}).strict();

/**
 * What a node reports about itself, every 30 seconds.
 *
 * Three of these fields existed before anything sent them: the endpoint parsed
 * active_calls, registrations and cdr_spool_depth, echoed two back to the
 * sender in the response, and stored none of them. node_status -- the table
 * whose own comment says it is "written by arc-agent heartbeats" -- had never
 * been written by anything at all.
 *
 * Every field is optional or defaulted. A heartbeat that reaches us at all is
 * worth recording, and an agent that cannot read its disk should still be able
 * to say it is alive.
 */
export const NodeHeartbeatSchema = z.object({
  node_name: z.string().max(64).optional(),
  software: z.record(z.string()).optional(),
  active_calls: z.number().int().min(0).default(0),
  registrations: z.number().int().min(0).default(0),
  cdr_spool_depth: z.number().int().min(0).default(0),

  // The host itself.
  load_1: z.number().min(0).max(10_000).optional(),
  load_5: z.number().min(0).max(10_000).optional(),
  mem_total_mb: z.number().int().min(0).optional(),
  mem_used_mb: z.number().int().min(0).optional(),
  disk_pct: z.number().min(0).max(100).nullable().optional(),
  uptime_sec: z.number().int().min(0).optional(),

  /** systemctl is-active per unit: {"freeswitch":"active", ...}. */
  services: z.record(z.string().max(32)).optional(),
  /** The agent's own startup assertions, which were visible only over loopback. */
  assertions: z.array(z.object({
    id: z.number().int(),
    name: z.string().max(120),
    ok: z.boolean(),
    detail: z.string().max(400),
  })).max(50).optional(),
  agent_version: z.string().max(64).nullable().optional(),
}).strict();

/**
 * A node's live registration snapshot.
 *
 * `contact_ip` is the HANDSET, parsed from the Contact URL by the agent — not
 * the network peer, which is the proxy. Reporting the peer would show every
 * extension registered from one address.
 */
export const NodeRegistrationSchema = z.object({
  reg_user: z.string().max(128),
  realm: z.string().max(253),
  contact_uri: z.string().max(512).nullable().optional(),
  contact_ip: z.string().max(64).nullable().optional(),
  contact_port: z.number().int().min(1).max(65535).nullable().optional(),
  transport: z.string().max(16).nullable().optional(),
  expires_at: TimestampSchema.nullable().optional(),
  user_agent: z.string().max(256).nullable().optional(),
  /** What the agent's ping found: reachable, unreachable or push (agent phoneping.ts). */
  ping_status: z.string().max(32).nullable().optional(),
  /** The ping's last round trip, in milliseconds. */
  ping_ms: z.number().int().min(0).max(120_000).nullable().optional(),
  call_id: z.string().max(256).nullable().optional(),
});

export const NodeRegistrationBatchSchema = z.object({
  registrations: z.array(NodeRegistrationSchema).max(20000),
}).strict();

/**
 * Live telephony state pushed by a node.
 *
 * Ephemeral by design: it is never stored, only held in memory and fanned out.
 * The durable record of a call is its CDR.
 */
export const NodeLiveSnapshotSchema = z.object({
  at: TimestampSchema,
  extensions: z.array(z.object({
    user: z.string().max(32),
    domain: z.string().max(253),
    state: z.enum(['idle', 'ringing', 'on_call']),
    since: z.number().int(),
    direction: z.enum(['inbound', 'outbound', 'internal']).nullable(),
    peer: z.string().max(64).nullable(),
  })).max(5000),
  queues: z.array(z.object({
    queue: z.string().max(160),
    name: z.string().max(160),
    domain: z.string().max(253),
    waiting: z.number().int().min(0),
    agents_total: z.number().int().min(0),
    agents_available: z.number().int().min(0),
    agents_on_call: z.number().int().min(0),
    calls_answered: z.number().int().min(0),
    longest_wait_sec: z.number().int().min(0),
    /**
     * Epoch ms the longest-waiting caller joined; null when nobody waits. Optional
     * so an agent from before the event-driven live state still validates.
     */
    longest_waiting_since: z.number().int().nullable().optional(),
  })).max(500),
}).strict();

/**
 * Queue events from a node.
 *
 * mod_callcenter's own vocabulary, passed through rather than translated: the
 * distinctions it draws between abandoning, timing out and being cancelled are
 * not recoverable once collapsed into our own words.
 */
export const NodeQueueEventBatchSchema = z.object({
  events: z.array(z.object({
    /**
     * `<token>@<sip_domain>`, or null for an agent-level event: mod_callcenter
     * sends status and state changes and max-no-answer per agent, with no
     * queue. `agent` then carries the domain the event is attributed by.
     */
    queue_key: z.string().max(200).nullable(),
    occurred_at: TimestampSchema,
    action: z.string().max(48),
    member_uuid: z.string().uuid().nullable().optional(),
    member_cid: z.string().max(64).nullable().optional(),
    agent: z.string().max(200).nullable().optional(),
    wait_sec: z.number().int().nullable().optional(),
    talk_sec: z.number().int().nullable().optional(),
    entry_position: z.number().int().nullable().optional(),
    exit_position: z.number().int().nullable().optional(),
    cause: z.string().max(64).nullable().optional(),
    cancel_reason: z.string().max(64).nullable().optional(),
    raw: z.record(z.string(), z.string()).optional(),
  })).max(500),
}).strict();
